// libs
import styled, { css } from 'styled-components'
// components
import { MenuItem } from 'components/UI/VerticalMenu/views'
import { TriggerWrapper } from 'components/UI/HoverablePopover'
// constants
import { NODE_CONTENT_RENDERER_SIZES } from 'constants/index'
// icons
import ActionsIconOrigin from '@mui/icons-material/VerticalSplit'
import AddIconOrigin from '@mui/icons-material/AddCircleOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIosRounded'
import LinkIconOrigin from 'src/icons/link-white.svg'
import MeasuresIconOrigin from 'src/icons/measures.svg'
import RemoveIconOrigin from '@mui/icons-material/RemoveCircleOutline'
// themes
import { colors } from 'themes/index'
// utils
import { isColorLight } from 'utils/tree'
import { darken, ellipsis, size, transparentize } from 'polished'

// types
interface IContentWrapperProps {
  $isKPI: boolean
  $isRoot: boolean
  $isAction: boolean
  $background: string
  $isObjective: boolean
}

interface INodeTitleWrapperProps {
  $isActive?: boolean
  $isObjective?: boolean
  $isReview?: boolean
  $isNodeReferenced?: boolean
}

interface ICenteredMenuItemProps {
  $isLink?: boolean
}

interface IIconsWrapperProps {
  $isKPI: boolean
  $isRoot: boolean
  $canDrag?: boolean
  $isAction: boolean
  $background: string
  $isObjective: boolean
}

interface ICollapseIconProps {
  $isKPI?: boolean
  $hidden?: boolean
  $isAction?: boolean
  $isExpanded: boolean
  $hasDescendants?: boolean
}

interface ILinkIconProps {
  $isLinked?: boolean
  $background?: string
  $isRootObjective?: boolean
  $isNodeReferenced?: boolean
  $isObjectivesModal?: boolean
}

interface ILinkIconWithCounterProps {
  $background?: string
  $isRootObjective?: boolean
}

const ActionsIcon = styled(ActionsIconOrigin)`
  && {
    margin-left: 2px;
  }
`

const AddIcon = styled(AddIconOrigin)`
  margin-right: 5px;
  ${size('18px')};

  path {
    fill: ${colors.blue500};
  }
`

const CenteredMenuItem = styled(MenuItem)<ICenteredMenuItemProps>`
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${colors.gray190};
  }

  ${({ $isLink }) =>
    $isLink &&
    css`
      background-color: ${colors.blue500};
      color: ${colors.white};

      &:hover {
        background-color: ${darken(0.07, colors.blue500)};
      }
    `};
`

const CollapseIcon = styled(ArrowForwardIcon)<ICollapseIconProps>`
  && {
    cursor: pointer;
    font-size: 20px;
    visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
    ${({ $isExpanded }) =>
      $isExpanded &&
      css`
        transform: rotate(90deg);
      `}

    ${({ $isAction, $isKPI }) =>
      ($isAction || $isKPI) &&
      css`
        font-size: 14px;
        margin-right: 5px;
        margin-left: 4px;
        color: ${colors.blue500};
      `};
  }
`

const Content = styled('div')`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 18px;
  height: 100%;
  width: 100%;

  ${TriggerWrapper} {
    flex: 1;
    overflow: hidden;
    height: 25px;
  }
`

const ContentInnerWrapper = styled('div')`
  position: relative;
  width: 100%;
`

const ContentWrapper = styled('div')<IContentWrapperProps>`
  align-items: center;
  color: ${({ $background }) => (isColorLight($background) ? colors.darkBlue500 : colors.white100)};
  display: flex;
  overflow: hidden;
  width: 100%;

  ${({ $isRoot }) =>
    !$isRoot &&
    css`
      margin-left: 2px;
    `};

  ${({ $isRoot, $isObjective }) =>
    !$isRoot &&
    $isObjective &&
    css`
      background: ${colors.gray190};
      border-bottom-right-radius: 5px;
      border-top-right-radius: 5px;
      color: ${colors.darkBlue500};

      &:hover {
        background: ${transparentize(0.2, colors.gray190)};
      }
    `};

  ${({ $isRoot, $isAction, $isKPI }) =>
    !$isRoot &&
    ($isAction || $isKPI) &&
    css`
      border: 1px solid ${colors.gray230};
      color: ${colors.darkBlue500};
    `};
`

const IconsWrapper = styled('div')<IIconsWrapperProps>`
  align-items: center;
  cursor: ${({ $canDrag }) => ($canDrag ? 'move' : 'not-allowed')};
  display: flex;
  justify-content: center;
  width: ${NODE_CONTENT_RENDERER_SIZES.NODE_HEIGHT}px;
  min-width: ${NODE_CONTENT_RENDERER_SIZES.NODE_HEIGHT}px;
  max-width: ${NODE_CONTENT_RENDERER_SIZES.NODE_HEIGHT}px;

  ${({ $background, $isObjective, $isRoot }) =>
    isColorLight($background) && !$isObjective && !$isRoot && `border: 1px solid ${colors.gray290}`};

  ${({ $isRoot, $isObjective, $background }) =>
    !$isRoot &&
    $isObjective &&
    css`
      background: ${$background};
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;

      &:hover {
        background: ${transparentize(0.2, $background)};
      }
    `};

  ${({ $isRoot, $isAction, $isKPI, $background }) =>
    !$isRoot &&
    ($isAction || $isKPI) &&
    css`
      background: ${$background};
      width: 22px;
      min-width: 22px;
      max-width: 22px;

      &:hover {
        background: ${transparentize(0.2, $background)};
      }
    `};

  & > * {
    color: ${colors.white};
  }
`

const LinkIcon = styled(LinkIconOrigin)<ILinkIconProps>`
  margin-right: 5px;
  ${size('18px')};

  ${({ $isLinked, $isObjectivesModal, $isRootObjective, $background }) =>
    $isLinked
      ? css`
          ${size('24px')};

          path {
            fill: ${$isRootObjective && !isColorLight($background) ? colors.white : colors.gray850};
          }
        `
      : css`
          path {
            fill: ${$isObjectivesModal && colors.blue500};
          }
        `};
`

const LinkIconWithCounter = styled('div')<ILinkIconWithCounterProps>`
  align-items: center;
  color: ${({ $isRootObjective, $background }) =>
    $isRootObjective && !isColorLight($background) ? colors.white : colors.gray850};
  display: flex;
  font-size: 13px;
  height: 42px;
  margin-left: auto;
  position: relative;

  > div {
    position: absolute;
    top: 0;
  }
`

const MeasuresIcon = styled(MeasuresIconOrigin)`
  && {
    margin-left: 4px;
  }
`

const NodeTitleWrapper = styled('span')<INodeTitleWrapperProps>`
  ${({ $isObjective }) => $isObjective && 'padding-left: 10px;'};
  ${({ $isNodeReferenced }) => $isNodeReferenced && `font-size: 16px`};

  ${ellipsis('100%')};

  ${({ $isReview }) =>
    $isReview &&
    css`
      font-size: 14px;
      margin-top: 2px;
    `};
`

const RemoveIcon = styled(RemoveIconOrigin)`
  margin-right: 5px;
  ${size('18px')};

  path {
    fill: ${colors.blue500};
  }
`

export {
  ActionsIcon,
  AddIcon,
  CenteredMenuItem,
  CollapseIcon,
  Content,
  ContentInnerWrapper,
  ContentWrapper,
  IconsWrapper,
  LinkIcon,
  LinkIconWithCounter,
  MeasuresIcon,
  NodeTitleWrapper,
  RemoveIcon
}
